import { date, Notify, QNotifyCreateOptions } from "quasar"
import { i18n } from "src/boot/i18n"
import { useUserStore } from "src/stores/userStore"
import { Icons } from "src/util/enums/fontAwesomeIcons"
import { CardNotificationEvent, NotificationActionMessage } from "src/util/enums/notifications"
import { undoNotificationDialog } from "src/util/functions/dialogs"
import { BatchErrors } from "src/util/types/dataModels/genericObject"
import { UserPermissionAccess, UserPermissionType } from "src/util/types/dataModels/user"
import { NotificationCardData, SystemNotificationCardData } from "src/util/types/notifications"

/**
 * Displays a notification message for successful action.
 *
 * @param message Text of the message.
 * @param html Allow HTML in the text message.
 */
export function showSuccessNotification(
  message: NotificationActionMessage | string,
  html?: boolean
) {
  const msg = Object.values(NotificationActionMessage).includes(message)
    ? translateMessage(message as NotificationActionMessage)
    : (message as string)

  Notify.create({
    type: "sfx-notification",
    message: msg,
    icon: Icons.SUCCEEDED,
    html: html,
  })
}

//Standard error notification with message
export function showErrorNotification(message: string) {
  Notify.create({
    type: "sfx-notification-error",
    message: message,
    icon: Icons.ERROR,
  })
}

//Standard info notification with message
export function showInfoNotification(message: string) {
  Notify.create({
    type: "sfx-notification",
    message: message,
    icon: Icons.INFORMATION,
  })
}

//Standard warning notification with message
export function showWarningNotification(message: string, html?: boolean) {
  Notify.create({
    type: "sfx-notification-warning",
    message: message,
    html: html,
  })
}

export function showDragAndDropFail() {
  const { t } = i18n.global

  Notify.create({
    type: "sfx-notification-accent",
    message: t("notifications.bad_file_read"),
  })
}

export function showDragAndDropEmptyFiles() {
  const { t } = i18n.global

  Notify.create({
    type: "sfx-notification-accent",
    message: t("notifications.files_empty"),
  })
}

export function showDragAndDropEmptyFile() {
  const { t } = i18n.global

  Notify.create({
    type: "sfx-notification-accent",
    message: t("notifications.file_empty"),
  })
}

export async function showMaxFileSizeExceededNotification() {
  const { t } = i18n.global
  const { MAX_UPLOAD_FILE_SIZE } = await import("src/util/constants/files")
  const maxFileSizeInMB = MAX_UPLOAD_FILE_SIZE / 1000000

  Notify.create({
    type: "sfx-notification-error",
    message: t("notifications.file_size_exceeded", { maxFileSize: maxFileSizeInMB }),
    icon: Icons.ERROR,
  })
}

export async function showMaxProfilePicSizeExceededNotification() {
  const { t } = i18n.global
  const { MAX_UPLOAD_PROFILE_PIC_SIZE } = await import("src/util/constants/files")
  const maxFileSizeInMB = MAX_UPLOAD_PROFILE_PIC_SIZE / 1000000

  Notify.create({
    type: "sfx-notification-error",
    message: t("notifications.file_size_exceeded", { maxFileSize: maxFileSizeInMB }),
    icon: Icons.ERROR,
  })
}

export async function showFileTypeNotAllowedNotification() {
  const { t } = i18n.global
  const { ATTACHMENT_ALLOWED_EXTENSIONS } = await import("src/util/constants/files")
  const allowedFileTypes = ATTACHMENT_ALLOWED_EXTENSIONS.join(", ")

  Notify.create({
    type: "sfx-notification-error",
    message: t("notifications.file_type_not_allowed", { fileTypes: allowedFileTypes }),
    icon: Icons.ERROR,
  })
}

export async function showFileTypeNotAllowedPartialNotification(files: File[]) {
  const { t } = i18n.global
  const { ATTACHMENT_ALLOWED_EXTENSIONS } = await import("src/util/constants/files")
  const allowedFileTypes = ATTACHMENT_ALLOWED_EXTENSIONS.join(", ")
  const fileTypesNotAllowed = t("notifications.file_type_not_allowed", {
    fileTypes: allowedFileTypes,
  })

  const fileNames = files.map((f) => f.name).join(", ")

  const filesNotUploaded = t("notifications.files_not_accepted", { files: fileNames })
  const message = fileTypesNotAllowed + ". " + filesNotUploaded

  Notify.create({
    type: "sfx-notification-error",
    message,
    icon: Icons.ERROR,
    multiLine: true,
    timeout: 10000, // 10sec
  })
}

export function showIssueDownloadingFileNotification() {
  const { t } = i18n.global

  Notify.create({
    type: "sfx-notification-error",
    message: t("notifications.bad_file_download"),
  })
}

export function showNotImplementedNotification() {
  const { t } = i18n.global

  Notify.create({
    type: "sfx-notification-accent",
    message: t("notifications.not_implemented"),
    icon: Icons.WRENCH,
  })
}

export function showUnsubmittedWorkNotification() {
  const msg = translateMessage(NotificationActionMessage.UNSUBMITTED_WORK)

  Notify.create({
    type: "sfx-notification",
    message: msg,
    icon: Icons.LIST_DISPLAY,
    iconColor: "navy-base",
  })
}

/**
 * This function has a notification that is infinitely long.
 * You NEED to manually close it by assigning the return value to c variable and calling it.
 *
 * @param singleImage
 */
export function showAttachmentUploadingNotification(singleImage: boolean) {
  const { t } = i18n.global

  const msg = singleImage
    ? t("notifications.image_uploading", 1)
    : t("notifications.image_uploading", 2)

  return Notify.create({
    type: "sfx-notification",
    message: msg,
    timeout: 0, // Infinite
  })
}
export function showConnectionLostNotification() {
  const { t } = i18n.global

  Notify.create({
    group: "group-internet",
    message: t("notifications.connection_lost"),
    color: "warning-back",
    textColor: "navy-base",
    iconColor: "warning",
    classes: "sfx-notification sfx-notification_lost-internet text-subtitle1 full-width",
    icon: Icons.OFFLINE,
    position: "top",
  })
}

export function showConnectionReestablishedNotification() {
  const { t } = i18n.global

  Notify.create({
    type: "sfx-notification",
    group: "group-internet",
    message: t("notifications.connection_reestablished"),
    icon: Icons.ONLINE,
    classes: "sfx-notification full-width",
    position: "top",
  })
}

export function showEasterEggNotification(message: string) {
  Notify.create({
    type: "sfx-notification",
    message: message,
    icon: Icons.WIZARD_WHITE,
  })
}

export function showRemovedTodoNotification(callbackFn: () => void, removedItem?: string) {
  const { t } = i18n.global
  const msg = t("notifications.removed_todo", { removedItem: removedItem })
  return undoNotificationDialog(msg, callbackFn)
}

export function showRevertSelectionNotification(callbackFn: () => void) {
  const { t } = i18n.global
  const msg = t("notifications.deselected_previous_selection")
  return undoNotificationDialog(msg, callbackFn)
}

export function identifyProposeUndo(callbackDismissFn: () => void) {
  const { t } = i18n.global
  const msg = t("notifications.identify_propose")
  return simpleUndoNotification(msg, callbackDismissFn)
}

export function identifyAssociatedUndo(callbackDismissFn: () => void) {
  const { t } = i18n.global
  const msg = t("notifications.identify_associated")
  return simpleUndoNotification(msg, callbackDismissFn)
}

export function identifyAssociationChangedUndo(callbackDismissFn: () => void) {
  const { t } = i18n.global
  const msg = t("notifications.identify_association_changed")
  return simpleUndoNotification(msg, callbackDismissFn)
}

export function identifyDissociateUndo(callbackDismissFn: () => void) {
  const { t } = i18n.global
  const msg = t("notifications.identify_dissociate")
  return simpleUndoNotification(msg, callbackDismissFn)
}

export function showUserNotification(user: string) {
  const { t } = i18n.global
  const userStore = useUserStore()
  const profilePicture = userStore.profilePicture
  const username = `<b>${user}</b>`
  const msg = t("notifications.direct_notification") + " " + username + "!"

  const notifyConfig: QNotifyCreateOptions = {
    type: "sfx-notification",
    html: true,
    message: msg,
    classes: "sfx-user-notification__message text-subtitle1",
  }

  if (profilePicture) {
    notifyConfig.avatar = profilePicture
  } else {
    notifyConfig.icon = "fa-solid fa-circle-user"
  }

  Notify.create(notifyConfig)
}

// Getting translated strings

function translateMessage(message: NotificationActionMessage): string {
  const { t } = i18n.global
  switch (message) {
    case NotificationActionMessage.PERFORMED_WORK:
      return t("notifications.performed_work")
    case NotificationActionMessage.ISSUES_RAISED:
      return t("notifications.issues_raised")
    case NotificationActionMessage.ALL_RAISED:
      return t("notifications.all_raised")
    case NotificationActionMessage.UNSUBMITTED_WORK:
      return t("notifications.unsubmitted_work")
    default:
      return t("notifications.new_notification")
  }
}

export function translateEventName(event: CardNotificationEvent): string {
  const { t } = i18n.global
  switch (event) {
    case CardNotificationEvent.ASSIGNED_WORK_ORDER:
      return t("notifications.assigned_work_order")
    case CardNotificationEvent.COMMENTED_WORK_ORDER:
      return t("notifications.commented_work_order")
    case CardNotificationEvent.COMMENTED_PERFORMED_WORK:
      return t("notifications.commented_performed_work")
    case CardNotificationEvent.REJECTED_ISSUE:
      return t("notifications.rejected_issue")
    case CardNotificationEvent.REJECTED_WORK:
      return t("notifications.rejected_work")
    case CardNotificationEvent.ACCEPTED_WORK:
      return t("notifications.accepted_work")
    case CardNotificationEvent.CORRECTED_ISSUE:
      return t("notifications.corrected_issue")
    case CardNotificationEvent.ADDED_ISSUE:
      return t("notifications.added_issue")
    default:
      return t("notifications.default_event_name")
  }
}

// TODO: To be removed when we have data from the server

export function getPriorityColor(priority: number) {
  switch (priority) {
    case 1:
      return "red"
    case 2:
      return "orange"
    case 3:
      return "blue"
    default:
      return "grey"
  }
}

/**
 * Exclude the system notifications whose endTime is in the past
 *
 * @param notification
 */
export function filterOldNotifications(notification: NotificationCardData) {
  return isSystemNotification(notification)
    ? new Date(notification.endTime) > new Date()
    : new Date(notification.timeSinceReceived) > date.subtractFromDate(new Date(), { days: 90 })
}

/**
 * Sort based on system notifications and then on date
 */
export function compareData(a: NotificationCardData, b: NotificationCardData) {
  // Check if either of them is not a system notification
  return Number(isSystemNotification(a)) ^ Number(isSystemNotification(b))
    ? Number(isSystemNotification(b)) - Number(isSystemNotification(a))
    : Number(new Date(b.timeSinceReceived)) - Number(new Date(a.timeSinceReceived))
}

export function isSystemNotification(
  notification: NotificationCardData
): notification is SystemNotificationCardData {
  return notification.isSystemCardNotification
}

export function showBatchErrors(errors: BatchErrors[], hasSuccessEntries: boolean) {
  const { t } = i18n.global

  const errorList = errors.map((e) => e.error).join("/n")
  const translationTemplate = hasSuccessEntries
    ? "notifications.batch_error_partial"
    : "notifications.batch_error_full"

  const message = t(translationTemplate, { entries: errorList })

  Notify.create({
    type: "sfx-notification-error",
    message,
    icon: Icons.ERROR,
    multiLine: true,
    timeout: 10000, // 10sec
  })
}

/**
 * Simple undo notification that will execute the function only on dismissal or timer running out,
 * and not on clicking "undo".
 *
 * Returns back the result of the dismissal function.
 *
 * @param msg
 * @param callbackDismissFn
 */
export function simpleUndoNotification<CallbackReturnType>(
  msg: string,
  callbackDismissFn: (...args: unknown[]) => CallbackReturnType
): Promise<ReturnType<typeof callbackDismissFn> | undefined> {
  let undoClickedStatus = false
  const undoClick = () => {
    undoClickedStatus = true
  }

  const dismissHandler = (): ReturnType<typeof callbackDismissFn> | undefined => {
    if (undoClickedStatus) return
    return callbackDismissFn()
  }

  return new Promise((resolve) =>
    undoNotificationDialog(msg, undoClick, dismissHandler).onOk(
      (data: ReturnType<typeof dismissHandler>) => resolve(data)
    )
  )
}

export function showNoAccess(
  permissionType: UserPermissionType,
  accessLevel: UserPermissionAccess
) {
  const { t } = i18n.global
  const noAccessBaseMessage = t("permissionNotifications.no_access")
  const translatedPermission = getPermissionTypeTranslation(permissionType)
  const translatedAccess = getAccessLevelTranslation(accessLevel)

  const message = `${noAccessBaseMessage}: ${translatedAccess} - ${translatedPermission}`

  return Notify.create({
    type: "sfx-notification-error",
    message,
    multiLine: true,
  })
}

function getAccessLevelTranslation(accessLevel: UserPermissionAccess): string {
  const { t } = i18n.global
  switch (accessLevel) {
    case "c":
      return t("permissionNotifications.access_create")
    case "r":
      return t("permissionNotifications.access_read")
    case "u":
      return t("permissionNotifications.access_update")
    case "d":
      return t("permissionNotifications.access_delete")
    case "+":
      return t("permissionNotifications.access_admin")
  }
}

function getPermissionTypeTranslation(permissionType: UserPermissionType): string {
  const { t } = i18n.global
  return t(`permissionNotifications.permission_${permissionType}`)
}
